// src/components/auth/SelectTierForm.tsx
"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import type { Product } from "@/types/product";
import { useUser } from "@/context/UserContext";
import { useEffect, useState } from "react";
import { generatePayfastSignature } from "@/app/actions/generatePayfastSignature";
import { PayFastForm, type PayfastData } from "@/components/profile/PayFastForm";
import { Loader2 } from "lucide-react";
import { getProductsClient } from "@/lib/firebase/services/productsClient";

// Annual subscription product codes — show "/year" not "/month"
const ANNUAL_SUBSCRIPTION_CODES = new Set([
  'RESTAURANT-ONCE-OFF-01',
  'STAY-ANNUAL-01',
  'FIXIT-ANNUAL-01',
  'PROSERV-ANNUAL-01',
  'RETAIL_ANNUAL',
]);

// **REVISED PRICE LOGIC**
const getPricingInfo = (product: Product) => {
    const isSubscription = product.IsSubscription;
    const isAnnual = ANNUAL_SUBSCRIPTION_CODES.has(product.ProductCode);
    const suffix = !isSubscription ? ' Per Year' : isAnnual ? '/year' : '/month';
    const hasDiscount = product.Discount > 0 && product.Discount < product.CostPrice;
    if (Number(product.CostPrice) === 0) {
        return { displayPrice: "Free", originalPrice: null };
    }

    // FIX: Combined the conditions into a single block to ensure consistent logic.
    if (product.ProductCode === 'ProfileViz' || product.ProductCode === 'BO-ONCE-01') {
      return { displayPrice: `R${product.CostPrice} Per Year Get 2 months FREE`, originalPrice: null };
    }

    if (product.ProductCode === 'DOMESTIC WORKERS') {
      return { displayPrice: `R${product.CostPrice} Per Year`, originalPrice: null };
    }

    if (hasDiscount) {
        return {
            displayPrice: `R${product.Discount}${suffix}`,
            originalPrice: `R${product.CostPrice}${suffix}`,
        };
    }

    return {
        displayPrice: `R${product.CostPrice}${suffix}`,
        originalPrice: null,
    };
};

type ProductFilter = 'all' | 'job-seeker' | 'business-owner' | 'domestic' | 'restaurant';

/**
 * Retired legacy plans and the retired zero-cost Starter plan must not be
 * offered for purchase. Filtered here at the single point where Firestore
 * products are rendered, so every filter tab below inherits the gate.
 */
const isSelectableProduct = (product: Product): boolean => {
  if (isRetiredProductCode(product.ProductCode)) return false;
  // The R0 "Starter (Free)" plan is retired alongside the paid legacy plans.
  if (Number(product.CostPrice) === 0) return false;
  return true;
};

const filterProducts = (products: Product[], filterType: ProductFilter): Product[] => {
  const available = products.filter(isSelectableProduct);

  if (filterType === 'all') {
    return available;
  }

  if (filterType === 'domestic') {
    return available.filter((product) => {
      const description = product.Description?.toLowerCase() ?? '';
      const code = product.ProductCode?.toLowerCase() ?? '';
      const cardDescription = product.cardDescription?.toLowerCase() ?? '';
      const allowedApps = (product.allowedApps ?? []).join(' ').toLowerCase();
      return [description, code, cardDescription, allowedApps].some((text) => text.includes('domestic'));
    });
  }

  if (filterType === 'restaurant') {
    return available.filter((product) => {
      const code = product.ProductCode?.toLowerCase() ?? '';
      const description = product.Description?.toLowerCase() ?? '';
      return code.includes('restaurant') || description.includes('restaurant');
    });
  }

  if (filterType === 'business-owner') {
    return available.filter((product) => {
      const description = product.Description?.toLowerCase() ?? '';
      return description.includes('business') || description.includes('biz');
    });
  }

  if (filterType === 'job-seeker') {
    return available.filter((product) => {
      const description = product.Description?.toLowerCase() ?? '';
      const code = product.ProductCode?.toLowerCase() ?? '';
      const cardDescription = product.cardDescription?.toLowerCase() ?? '';
      const allowedApps = (product.allowedApps ?? []).join(' ').toLowerCase();
      const navigationPath = product.navigationPath?.toLowerCase() ?? '';
      const allText = [description, code, cardDescription, allowedApps, navigationPath];
      return allText.some(
        (text) =>
          text.includes('job seeker') ||
          text.includes('job-seeker') ||
          text.includes('profileviz') ||
          text.includes('profileplus'),
      );
    });
  }

  return products.filter((product) => {
    const description = product.Description?.toLowerCase() ?? '';
    const code = product.ProductCode?.toLowerCase() ?? '';
    const cardDescription = product.cardDescription?.toLowerCase() ?? '';
    const allowedApps = (product.allowedApps ?? []).join(' ').toLowerCase();
    const isDomestic = [description, code, cardDescription, allowedApps].some((text) => text.includes('domestic'));
    const isRestaurant = [description, code, cardDescription, allowedApps].some((text) => text.includes('restaurant'));

    return !isDomestic && !isRestaurant && !description.includes('business') && !description.includes('biz');
  });
};

import Head from 'next/head';
import { isRetiredProductCode } from '@/lib/payfast/productRules';

export function SelectTierForm({ products, filterType = 'all' }: { products: Product[]; filterType?: ProductFilter }) {
  const { toast } = useToast();
  const router = useRouter();
  const { user } = useUser();
  const [upgradingProductId, setUpgradingProductId] = useState<string | null>(null);
  const [payfastData, setPayfastData] = useState<PayfastData | null>(null);
  const [displayProducts, setDisplayProducts] = useState<Product[]>(() => filterProducts(products, filterType));
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  useEffect(() => {
    setDisplayProducts(filterProducts(products, filterType));
  }, [products, filterType]);

  useEffect(() => {
    if (products.length > 0) {
      return;
    }

    let isActive = true;
    setIsLoadingProducts(true);

    getProductsClient()
      .then((fetched) => {
        if (isActive) {
          setDisplayProducts(filterProducts(fetched, filterType));
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingProducts(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [products.length, filterType]);

  const resolveNavigationPath = (product: Product): string | null => {
    const configuredPath = product.navigationPath?.trim();
    if (configuredPath) {
      return configuredPath;
    }

    const code = product.ProductCode?.toLowerCase() ?? '';
    const description = product.Description?.toLowerCase() ?? '';

    // Safe fallback for products that were created without navigationPath in admin.
    if (filterType === 'restaurant' || code.includes('restaurant') || description.includes('restaurant')) {
      return '/create-profile/restaurant-owner-premium';
    }

    return null;
  };

  async function handleChoosePlan(product: Product) {
    // Always redirect to the profile creation form, never directly to payment
    const path = resolveNavigationPath(product);
    if (!path) {
      console.error("Product is missing 'navigationPath' field:", product);
      toast({
        title: "Configuration Error",
        description: `The product \"${product.Description}\" is not configured correctly. Please contact support.`,
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Plan Selected",
      description: `Redirecting to create your ${product.Description} profile...`,
      duration: 5000,
    });
    router.push(path);
  }

  // If payfastData is ready, show the payment processing view.
  if (payfastData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Card className="w-full max-w-lg mx-auto shadow-xl my-8">
          <CardHeader className="text-center">
            <CardTitle>Proceed to Payment</CardTitle>
            <CardDescription>You are being redirected to our secure payment partner, PayFast, to complete your upgrade.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <PayFastForm payfastData={payfastData} />
            <p className="text-xs text-muted-foreground mt-2">If you are not redirected, please click the button above.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sortedProducts = [...displayProducts].sort((a, b) => {
    if (a.CostPrice === 0) return 1;
    if (b.CostPrice === 0) return -1;
    return a.CostPrice - b.CostPrice;
  });

  const isSingleCard = sortedProducts.length === 1;
  const isCenteredSingle = isSingleCard && (filterType === 'domestic' || filterType === 'restaurant');
  const gridClassName = `grid grid-cols-1 gap-6 md:gap-8 ${isCenteredSingle ? 'md:grid-cols-1 lg:grid-cols-1 justify-items-center' : 'md:grid-cols-2 lg:grid-cols-3'}`;

  const getPlanValueSummary = (product: Product) => {
    const code = product.ProductCode?.toLowerCase() ?? '';
    const description = product.Description?.toLowerCase() ?? '';

    if (filterType === 'restaurant' || code.includes('restaurant') || description.includes('restaurant')) {
      return 'Show your menu, win more bookings, and give diners the confidence to choose you faster.';
    }

    return null;
  };

  // --- Structured Data for Google (schema.org Product/Offer) ---
  const structuredData = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': sortedProducts.map((product) => {
      const { displayPrice } = getPricingInfo(product);
      // Extract price as number (e.g., R55/month -> 55)
      const priceMatch = displayPrice.match(/R(\d+(?:\.\d+)?)/);
      const price = priceMatch ? priceMatch[1] : '0';
      return {
        '@type': 'Product',
        name: product.Description,
        description: product.cardDescription || product.Description,
        image: product.ImageUrl,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'ZAR',
          price,
          availability: 'https://schema.org/InStock',
          url: typeof window !== 'undefined' ? window.location.href : '',
        },
      };
    }),
  });

  return (
    <>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
      </Head>
      <div className="bg-white">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-10 md:mb-12">
          {filterType === 'job-seeker' ? (
            <>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3" style={{ color: "#001489" }}>
                Build a Profile That Gets You Hired
              </h1>
              <p className="text-base md:text-lg max-w-2xl mx-auto mb-3" style={{ color: "#0077B6" }}>
                Transform your profile into a powerful personal brand that recruiters can’t ignore.
              </p>
            </>
          ) : filterType === 'domestic' ? (
            <>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3" style={{ color: "#001489" }}>
                Empowering Domestic Excellence
              </h1>
              <p className="text-base md:text-lg max-w-2xl mx-auto mb-3" style={{ color: "#0077B6" }}>
                Highlight your expertise and get discovered by families who need your help.
              </p>
            </>
          ) : filterType === 'restaurant' ? (
            <>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3" style={{ color: "#001489" }}>
                Dining & Eateries
              </h1>
              <p className="text-base md:text-lg max-w-2xl mx-auto mb-3" style={{ color: "#0077B6" }}>
                One powerful profile. Attract guests, build your team, and scale your brand, all from a single page.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3" style={{ color: "#001489" }}>
                Elevate Your Business Performance Professional
              </h1>
              <p className="text-base md:text-lg max-w-2xl mx-auto mb-3" style={{ color: "#0077B6" }}>
                Growth Tiers: Built for Business Owners
              </p>
            </>
          )}
        </div>

        <div className={gridClassName}>
          {isLoadingProducts && sortedProducts.length === 0 && (
            <Card className="flex items-center justify-center py-12 shadow-lg border-none">
              <CardContent className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading plans...
              </CardContent>
            </Card>
          )}
          {sortedProducts.map((product) => {
            const { displayPrice, originalPrice } = getPricingInfo(product);
            const isLoading = upgradingProductId === product.id;
            const planValueSummary = getPlanValueSummary(product);

            return (
              <Card key={product.id} className={`flex flex-col overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-none hover:-translate-y-1${isCenteredSingle ? ' w-full max-w-md' : ''}`}>
                <div className="relative w-full h-48 md:h-56">
                  {product.ImageUrl ? (
                    <Image
                      src={product.ImageUrl}
                      alt={product.Description}
                      fill
                      style={{ objectFit: 'cover' }}
                      className="bg-muted"
                      onError={(e) => console.error(`Error loading image for ${product.Description}: ${product.ImageUrl}`, e)}
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground">No Image</span>
                    </div>
                  )}
                </div>
                <CardHeader className="p-5">
                  <CardTitle className="text-xl font-semibold text-foreground">{product.Description}</CardTitle>
                  <CardDescription className="text-primary font-medium text-lg mt-1 h-12">
                    <span>{displayPrice}</span>
                    {originalPrice && (
                      <span className="ml-2 line-through text-muted-foreground text-base">
                        {originalPrice}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-5 pt-0 grow">
                  {planValueSummary ? (
                    <p className="mb-3 text-sm leading-relaxed text-slate-700">
                      {planValueSummary}
                    </p>
                  ) : null}
                  <p className="text-sm text-muted-foreground">{product.cardDescription || product.Description}</p>
                </CardContent>
                <CardFooter className="p-5 pt-0 mt-auto">
                  <Button
                    className="w-full"
                    style={{ backgroundColor: "#FFB81C", color: "#ffffff" }}
                    onClick={() => handleChoosePlan(product)}
                    disabled={upgradingProductId !== null}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Preparing...
                      </>
                    ) : 'Choose Plan'}
                  </Button>
                </CardFooter>
              </Card>
            )
          })}
        </div>
        <p className="mt-12 text-center text-sm text-muted-foreground">
          You can change your plan later if needed. Need help deciding?{" "}
          <Link href="/contact" className="font-medium text-primary hover:underline">
            Contact us
          </Link>
        </p>
      </div>
    </div>
    </>
  );
}
